// Cloudinary removal — Plan 1: URL helpers for derived variants and crops.
import type { Variant, AspectRatioKey } from './types';

/**
 * A variant file (`{assetId}_lg.jpg`) only exists for assets we uploaded
 * through our own pipeline WITH variant generation on. It does NOT exist for:
 *   - legacy Cloudinary URLs (different host entirely),
 *   - assets uploaded single-file (generateVariants off — the default),
 *   - assets bulk-migrated base-only,
 *   - external / non-media URLs.
 * For all of those, deriving `_lg`/`_thumb` would 404. So we only rewrite URLs
 * that live in our Supabase `media`/`public-uploads` storage path. Anything else
 * is returned UNCHANGED — the caller renders the base URL, which always exists.
 *
 * NOTE: even for a real media URL we cannot know synchronously whether a given
 * variant file was actually generated. Callers that render a derived variant
 * should add an `<img onError>` fallback to the base URL. See FrameStylePreview.
 */
function isDerivableMediaUrl(url: string): boolean {
  // Supabase Storage object URLs for our buckets (public or signed).
  if (!/\/storage\/v1\/object\/(public|sign)\/(media|public-uploads)\//.test(url)) {
    return false;
  }
  // Must have a real file extension to splice a suffix before.
  const lastSlash = url.lastIndexOf('/');
  const lastDot = url.lastIndexOf('.');
  return lastDot > lastSlash;
}

function insertSuffix(url: string, suffix: string): string {
  const lastDot = url.lastIndexOf('.');
  return url.slice(0, lastDot) + suffix + url.slice(lastDot);
}

/**
 * Derive the URL for a size variant. Returns the base URL unchanged when the
 * variant cannot safely be derived (legacy/external/single-file assets), so it
 * never produces a 404-ing URL.
 */
export function variantUrl(baseUrl: string, variant: Variant): string {
  if (!baseUrl || !isDerivableMediaUrl(baseUrl)) return baseUrl;
  return insertSuffix(baseUrl, `_${variant}`);
}

/**
 * Derive a cropped-derivative URL. Same legacy-safe contract as variantUrl.
 */
export function cropUrl(baseUrl: string, ratio: AspectRatioKey): string {
  if (!baseUrl || !isDerivableMediaUrl(baseUrl)) return baseUrl;
  return insertSuffix(baseUrl, `_crop-${ratio}`);
}

/**
 * Build a srcset over the variants we actually generate (thumb + lg). For
 * non-derivable URLs returns just the base URL so the browser has one valid
 * candidate.
 */
export function srcsetFor(baseUrl: string): string {
  if (!baseUrl || !isDerivableMediaUrl(baseUrl)) return baseUrl;
  return [
    `${variantUrl(baseUrl, 'thumb')} 400w`,
    `${variantUrl(baseUrl, 'lg')} 1600w`,
  ].join(', ');
}

export function isLegacyCloudinaryUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.includes('res.cloudinary.com');
}

/**
 * True only for a genuine Supabase Storage object URL. Used as a host invariant
 * when copying an image between records: an expiring vendor URL (Templated.io S3
 * `templated-assets.s3...` or KIE `tempfile.aiquickdraw.com`) must never be
 * propagated into a post/idea, because it 404s within hours and strands the image.
 * Mirrors supabase/functions/_shared/media/hostGuard.ts - keep the two in sync.
 */
export function isSupabaseHostedUrl(url: string | null | undefined): boolean {
  return typeof url === 'string'
    && /^https:\/\/[a-z0-9-]+\.supabase\.co\/storage\/v1\/object\//.test(url);
}

/**
 * True only for the KNOWN ephemeral image hosts that expire within hours:
 * KIE (tempfile.aiquickdraw.com) and Templated.io (templated-assets.s3...).
 * Use on the READ/COPY side to drop ONLY these expiring URLs. Durable URLs -
 * Supabase, legacy Cloudinary (kept alive by the migration), Google, and other
 * user-supplied hosts - must NOT be dropped, since they still render.
 * Mirrors supabase/functions/_shared/media/hostGuard.ts - keep in sync.
 */
export function isEphemeralVendorUrl(url: string | null | undefined): boolean {
  return typeof url === 'string'
    && (url.includes('tempfile.aiquickdraw.com')
      || /templated-assets\.s3[.-][^/]*\.amazonaws\.com/.test(url)
      || url.includes('templated-assets.s3.amazonaws.com'));
}
